import { Component, type ReactNode } from 'react';

import { captureException, captureMessage, showReportDialog } from 'src/lib/sentry';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

interface ErrorInfo {
  componentStack: string;
}

const reportToSentry = (error: Error, errorInfo: ErrorInfo): void => {
  if (error.name === 'ChunkLoadError') {
    captureMessage('Error loading FE bundle chunk', {
      level: 'warning',
      extra: {
        ...errorInfo,
        error: error.toString(),
        source: 'components/ErrorBoundary.tsx',
      },
    });
  } else {
    captureException(error, {
      extra: {
        ...errorInfo,
        source: 'ErrorBoundary',
      },
      tags: {
        transaction: 'ErrorBoundary',
      },
    });
  }
};

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    reportToSentry(error, errorInfo);
  }

  render(): ReactNode {
    const { children } = this.props;
    const { error } = this.state;

    if (error) {
      // render fallback UI
      return (
        <div className="snap">
          <p>Oh no, something went wrong.</p>
          <p>Our engineers have been notified. Please try again soon.</p>

          <button
            type="button"
            onClick={showReportDialog}
          >
            Report
          </button>
        </div>
      );
    }

    return children;
  }
}
